import { allEnumValues, MenuCoverPromotionType, MenuCoverType, VitrineCoverBlockType, VitrineCoverBottomBlockContent, VitrinePathwayBlockType, } from '@biteinc/enums';
import { AbstractModelSchema } from './abstract_model_schema.js';
import { videoSchemaBuilder } from './video_schema.js';
export const menuCoverSchema = {
    displayName: 'menu cover',
    type: 'object',
    fields: {
        ...AbstractModelSchema.fields,
        orgId: {
            type: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        locationId: {
            type: 'mongoId',
            isHidden: true,
            isReadOnly: true,
        },
        name: {
            type: 'shortString',
            required: true,
        },
        coverType: {
            displayName: 'Cover Type',
            type: 'stringEnum',
            enum: MenuCoverType,
            ui: 'dropdown',
            required: true,
        },
        coverImg: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'iOS Image',
            maxFileSizeMb: 20,
            size: {
                width: 2048,
                height: 2732,
            },
            conditions: [
                {
                    field: 'coverType',
                    values: [MenuCoverType.Gcn],
                },
            ],
        },
        coverImg1080: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: '1080p Image',
            maxFileSizeMb: 20,
            size: {
                width: 1080,
                height: 1920,
            },
            conditions: [
                {
                    field: 'coverType',
                    values: [MenuCoverType.Gcn],
                },
            ],
        },
        coverImgWithPromos: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'iOS Image w/ Promos',
            maxFileSizeMb: 20,
            size: {
                width: 2048,
                height: 1432,
            },
            conditions: [
                {
                    field: 'coverType',
                    values: [MenuCoverType.Gcn],
                },
            ],
        },
        coverImg1080WithPromos: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: '1080p Image w/ Promos',
            maxFileSizeMb: 20,
            size: {
                width: 1080,
                height: 1270,
            },
            conditions: [
                {
                    field: 'coverType',
                    values: [MenuCoverType.Gcn],
                },
            ],
        },
        fallbackCoverImg: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 1,
            displayName: 'Fallback Cover Image',
            maxFileSizeMb: 20,
            // Not `required`: a cover built on a large media block is exempt, and that depends on
            // portraitBlocks, which schema conditions cannot express. The controllers enforce it.
            size: {
                width: 1080,
                height: 1750,
            },
            tooltip: {
                text: 'This image will only be used if the normal cover configuration is no longer viable',
            },
            conditions: [
                {
                    field: 'coverType',
                    values: [MenuCoverType.VitrinePortrait],
                },
            ],
        },
        menuCoverPromoIds: {
            type: 'array',
            elementType: 'mongoId',
            ui: 'table',
            displayName: 'Promos',
            placeholder: 'Select a promo',
            conditions: [
                {
                    field: 'coverType',
                    values: [MenuCoverType.Gcn],
                },
            ],
        },
        showLanguageSelectionButtons: {
            displayName: 'Show Language Selection Buttons',
            type: 'bool',
            conditions: [
                {
                    field: 'coverType',
                    values: [MenuCoverType.Gcn],
                },
            ],
        },
        portraitBlocks: {
            displayName: 'Portrait Blocks',
            type: 'array',
            elementType: 'object',
            ui: 'accordion',
            uiSubtype: 'MenuCoverDisplayBlocks',
            conditions: [
                {
                    field: 'coverType',
                    values: [MenuCoverType.VitrinePortrait],
                },
            ],
            fields: {
                type: {
                    displayName: 'Block Type',
                    type: 'stringEnum',
                    enum: VitrineCoverBlockType,
                    ui: 'dropdown',
                    required: true,
                    isHidden: true,
                },
                displayType: {
                    displayName: 'Display Type',
                    type: 'stringEnum',
                    enum: MenuCoverPromotionType,
                    values: allEnumValues(MenuCoverPromotionType).filter((promotionType) => promotionType !== MenuCoverPromotionType.GCN),
                    ui: 'dropdown',
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.Pathway],
                        },
                    ],
                    required: true,
                },
                imagePortraitXs: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 1,
                    displayName: 'Extra Small Image',
                    maxFileSizeMb: 20,
                    keepAspectRatio: 'contain',
                    required: true,
                    size: {
                        width: 1080,
                        height: 610,
                    },
                    canHaveLocalizationOverrides: true,
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.CoverImageExtraSmall],
                        },
                    ],
                },
                videoPortraitXs: {
                    displayName: 'Extra Small Video',
                    type: 'object',
                    required: true,
                    fields: videoSchemaBuilder({
                        posterImageDimensions: {
                            width: 1080,
                            height: 610,
                        },
                        thumbnailDimensions: null,
                    }),
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.CoverVideoExtraSmall],
                        },
                    ],
                },
                imagePortraitSm: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 1,
                    displayName: 'Small Image',
                    maxFileSizeMb: 20,
                    keepAspectRatio: 'contain',
                    required: true,
                    size: {
                        width: 1080,
                        height: 1026,
                    },
                    canHaveLocalizationOverrides: true,
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.CoverImageSmall],
                        },
                    ],
                },
                videoPortraitSm: {
                    displayName: 'Small Video',
                    type: 'object',
                    required: true,
                    fields: videoSchemaBuilder({
                        posterImageDimensions: {
                            width: 1080,
                            height: 1026,
                        },
                        thumbnailDimensions: null,
                    }),
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.CoverVideoSmall],
                        },
                    ],
                },
                imagePortraitMd: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 1,
                    required: true,
                    displayName: 'Medium Image',
                    maxFileSizeMb: 20,
                    size: {
                        width: 1080,
                        height: 1388,
                    },
                    canHaveLocalizationOverrides: true,
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.CoverImageMedium],
                        },
                    ],
                },
                videoPortraitMd: {
                    displayName: 'Medium Video',
                    type: 'object',
                    required: true,
                    fields: videoSchemaBuilder({
                        posterImageDimensions: {
                            width: 1080,
                            height: 1388,
                        },
                        thumbnailDimensions: null,
                    }),
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.CoverVideoMedium],
                        },
                    ],
                },
                imagePortraitLg: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 1,
                    displayName: 'Large Image',
                    maxFileSizeMb: 20,
                    required: true,
                    size: {
                        width: 1080,
                        height: 1750,
                    },
                    canHaveLocalizationOverrides: true,
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.CoverImageLarge],
                        },
                    ],
                },
                videoPortraitLg: {
                    displayName: 'Large Video',
                    type: 'object',
                    required: true,
                    fields: videoSchemaBuilder({
                        posterImageDimensions: {
                            width: 1080,
                            height: 1750,
                        },
                        thumbnailDimensions: null,
                    }),
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.CoverVideoLarge],
                        },
                    ],
                },
                promotionType: {
                    displayName: 'Promotion Type',
                    required: true,
                    type: 'stringEnum',
                    enum: MenuCoverPromotionType,
                    ui: 'dropdown',
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.PromoMedium, VitrineCoverBlockType.PromoSmall],
                        },
                    ],
                },
                menuCoverPromoIds: {
                    required: true,
                    type: 'array',
                    elementType: 'mongoId',
                    ui: 'table',
                    displayName: 'Promos',
                    placeholder: 'Select a promo',
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.PromoMedium, VitrineCoverBlockType.PromoSmall],
                        },
                    ],
                },
                content: {
                    type: 'stringEnum',
                    enum: VitrineCoverBottomBlockContent,
                    ui: 'dropdown',
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.BottomSection],
                        },
                    ],
                },
                pathways: {
                    displayName: 'Pathway Blocks',
                    type: 'array',
                    elementType: 'object',
                    ui: 'accordion',
                    uiSubtype: 'MenuCoverPathwayBlocks',
                    conditions: [
                        {
                            field: 'type',
                            values: [VitrineCoverBlockType.Pathway],
                        },
                    ],
                    fields: {
                        linkType: {
                            displayName: 'Pathway Type',
                            type: 'stringEnum',
                            enum: VitrinePathwayBlockType,
                            ui: 'dropdown',
                            required: true,
                        },
                        title: {
                            type: 'shortString',
                        },
                        subtitle: {
                            type: 'shortString',
                        },
                        image: {
                            type: 'array',
                            elementType: 'object',
                            elementSubtype: 'Image',
                            elementUniqueIdFieldName: 'origUrl',
                            maxCount: 1,
                            maxFileSizeMb: 20,
                            required: true,
                            canHaveLocalizationOverrides: true,
                            size: {
                                width: 1080,
                                height: 1026,
                            },
                        },
                    },
                },
            },
        },
    },
};
//# sourceMappingURL=menu_cover_schema.js.map